import styled from 'styled-components';
import {Section, SectionHeader, PropertyName, ReadOnlyProperty} from '../../../components/Form/FormStyles';
import {EditedProject} from '../EditedProject';
import {PlantingProjectType} from '../../../entities';

const MethodNote = styled.span`
    font-size: 0.8em;
    color: rgb(140, 140, 140);
    margin-left: 0.4rem;
`;

function BenefitRow({label, value, note}: {label: string; value: number | null; note?: string}) {
    return (
        <ReadOnlyProperty>
            <PropertyName>{label}: </PropertyName>
            {value != null ? value : '—'}
            {note && <MethodNote>({note})</MethodNote>}
        </ReadOnlyProperty>
    );
}


export function BenefitsSection({project}: {project: EditedProject}) {
    const projectType = project.ProjectType.current;

    if (projectType === PlantingProjectType.Reforestation) {
        const usePerAcre = project.VWBACalcMethod != null && project.VWBACalcMethod !== 'iTree';
        const waterValue = usePerAcre
            ? project.GallonsTotalRunoffAvoidedPerAcreReforestation
            : project.GallonsTotalRunoffAvoidedPerTreeReforestation;
        const waterLabel = usePerAcre ? 'Water runoff avoided (gal/acre)' : 'Water runoff avoided (gal/tree)';

        return (
            <Section>
                <SectionHeader>Benefits</SectionHeader>
                <BenefitRow label={waterLabel} value={waterValue} note={usePerAcre ? project.VWBACalcMethod : undefined} />
                <BenefitRow label="Carbon Sequestered (tons/tree)" value={project.TonsCarbonPerTreeReforestation} />
                <BenefitRow label="Air pollutant removed (tons/tree)" value={project.TonsAirPollutionPerTreeReforestation} />
            </Section>
        );
    }

    if (projectType === PlantingProjectType.CommunityEvent) {
        return (
            <Section>
                <SectionHeader>Benefits</SectionHeader>
                <BenefitRow label="Water runoff avoided per planted tree (gal/tree)" value={project.GallonsTotalRunoffAvoidedPerTreeEventPlanted} />
                <BenefitRow label="Water runoff avoided per distributed tree (gal/tree)" value={project.GallonsTotalRunoffAvoidedPerTreeEventDistributed} />
                <BenefitRow label="Carbon Sequestered per planted tree (tons/tree)" value={project.TonsCarbonPerTreeEventPlanted} />
                <BenefitRow label="Carbon Sequestered per distributed tree (tons/tree)" value={project.TonsCarbonPerTreeEventDistributed} />
                <BenefitRow label="Air pollutant removed per planted tree (tons/tree)" value={project.TonsAirPollutionPerTreeEventPlanted} />
                <BenefitRow label="Air pollutant removed per distributed tree (tons/tree)" value={project.TonsAirPollutionPerTreeEventDistributed} />
            </Section>
        );
    }

    if (projectType === PlantingProjectType.MembershipTrees) {
        return (
            <Section>
                <SectionHeader>Benefits</SectionHeader>
                <BenefitRow label="Water runoff avoided (gal/tree)" value={project.GallonsTotalRunoffAvoidedPerTreeMembershipTrees} />
                <BenefitRow label="Carbon Sequestered (tons/tree)" value={project.TonsCarbonPerTreeMembershipTrees} />
                <BenefitRow label="Air pollutant removed (tons/tree)" value={project.TonsAirPollutionPerTreeMembershipTrees} />
            </Section>
        );
    }

    if (projectType === PlantingProjectType.CommunityTreeDistributor) {
        return (
            <Section>
                <SectionHeader>Benefits</SectionHeader>
                <BenefitRow label="Water runoff avoided (gal)" value={project.GallonsRunoffAvoidedCommunityTreeDistributor} />
                <BenefitRow label="Carbon Sequestered (tons)" value={project.TonsCarbonCommunityTreeDistributor} />
                <BenefitRow label="Air pollutant removed (tons)" value={project.TonsAirPollutionCommunityTreeDistributor} />
            </Section>
        );
    }

    return null;
}
